import { useState, useEffect, useRef } from 'react';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LockIcon from '@mui/icons-material/Lock';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import LinkIcon from '@mui/icons-material/Link';
import ArticleIcon from '@mui/icons-material/Article';
import DevicesIcon from '@mui/icons-material/Devices';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LanguageIcon from '@mui/icons-material/Language';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageIcon from '@mui/icons-material/Image';
import ConfirmDialog from './ConfirmDialog';
import { uploadFile } from '../../services/uploadApi';
import { useDriveMetadata } from '../../hooks/useDriveMetadata';
import type { CardMetadata } from '../../types/card';
import type { PageType } from '../../types/nav';

const TYPE_ICONS: Record<PageType, { Icon: React.ElementType; color: string }> = {
  doc: { Icon: DescriptionIcon, color: '#2563EB' },
  sheet: { Icon: TableChartIcon, color: '#10B981' },
  slides: { Icon: SlideshowIcon, color: '#F59E0B' },
  guide: { Icon: ArticleIcon, color: '#2563EB' },
  prototype: { Icon: DevicesIcon, color: '#8B5CF6' },
  reference: { Icon: MenuBookIcon, color: '#0D9488' },
  external: { Icon: LinkIcon, color: '#6B7280' },
  website: { Icon: LanguageIcon, color: '#2563EB' },
  upload: { Icon: UploadFileIcon, color: '#8B5CF6' },
};

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

function formatDate(ts?: { toDate: () => Date }): string {
  if (!ts) return '';
  return dateFormatter.format(ts.toDate());
}

interface Props {
  card: CardMetadata;
  onUpdate?: (id: string, updates: { title: string; summary: string; url: string }) => Promise<void>;
  onSetThumbnail?: (id: string, thumbnailUrl: string) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
}

export default function PortalCard({ card, onUpdate, onSetThumbnail, onDelete }: Props) {
  const { refreshCardMetadata } = useDriveMetadata();
  const refreshAttempted = useRef(false);
  const thumbInputRef = useRef<HTMLInputElement>(null);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(card.title);
  const [editSummary, setEditSummary] = useState(card.summary);
  const [editUrl, setEditUrl] = useState(card.url);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);

  useEffect(() => {
    if (refreshAttempted.current) return;
    if (!card.driveDocId) return;
    refreshAttempted.current = true;
    refreshCardMetadata(card).catch(() => {});
  }, [card, refreshCardMetadata]);

  const typeInfo = TYPE_ICONS[card.type] ?? TYPE_ICONS.external;
  const TypeIcon = typeInfo.Icon;

  // Thumbnail source: prefer a stored thumbnail (uploads / custom); otherwise fall
  // back to the thum.io screenshot service for plain website URLs only.
  const isDriveUrl = !!card.url && (card.url.includes('docs.google.com') || card.url.includes('drive.google.com'));
  const thumbSrc = card.thumbnailUrl
    ? card.thumbnailUrl
    : card.type === 'website' && card.url && !isDriveUrl
    ? `https://image.thum.io/get/width/600/crop/375/${card.url}`
    : null;
  const showImage = !!thumbSrc && !thumbFailed;

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const handleMenuClose = () => setMenuAnchor(null);

  const handleThumbnailMenuClick = () => {
    handleMenuClose();
    thumbInputRef.current?.click();
  };

  const handleThumbnailSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !onSetThumbnail) return;
    setThumbUploading(true);
    try {
      const result = await uploadFile(file);
      if (result.ok) {
        // Prefer the generated (sized) thumbnail; fall back to the uploaded image itself.
        const tUrl = result.data.thumbnailUrl ?? result.data.url;
        await onSetThumbnail(card.id, tUrl);
        setThumbFailed(false);
      }
    } finally {
      setThumbUploading(false);
    }
  };

  const handleEditStart = () => {
    handleMenuClose();
    setEditTitle(card.title);
    setEditSummary(card.summary);
    setEditUrl(card.url);
    setEditing(true);
  };

  const handleEditCancel = () => {
    setEditing(false);
  };

  const handleEditSave = async () => {
    if (!editTitle.trim() || !onUpdate) return;
    setSaving(true);
    try {
      await onUpdate(card.id, {
        title: editTitle.trim(),
        summary: editSummary.trim(),
        url: editUrl.trim(),
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setConfirmOpen(false);
    if (onDelete) await onDelete(card.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') handleEditCancel();
  };

  if (editing) {
    return (
      <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          size="small"
          placeholder="Title"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          fullWidth
        />
        <TextField
          size="small"
          placeholder="Description"
          value={editSummary}
          onChange={(e) => setEditSummary(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          multiline
          minRows={2}
        />
        <TextField
          size="small"
          placeholder="Link (optional)"
          value={editUrl}
          onChange={(e) => setEditUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
        />
        <Box display="flex" gap={1} justifyContent="flex-end">
          <Button size="small" onClick={handleEditCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleEditSave}
            disabled={saving || !editTitle.trim()}
          >
            {saving ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </Box>
      </Card>
    );
  }

  return (
    <>
      <Card
        sx={{
          p: 0,
          position: 'relative',
          '&:hover .card-menu-btn': { opacity: 1 },
        }}
      >
        {(onUpdate || onDelete) && (
          <IconButton
            className="card-menu-btn"
            size="small"
            onClick={handleMenuOpen}
            aria-label="Card actions"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 2,
              opacity: 0,
              transition: 'opacity 150ms ease-in-out',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}

        {onSetThumbnail && (
          <input
            ref={thumbInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.gif,.webp,.svg"
            hidden
            onChange={handleThumbnailSelect}
          />
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {onUpdate && (
            <MenuItem onClick={handleEditStart}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {onSetThumbnail && (
            <MenuItem onClick={handleThumbnailMenuClick}>
              <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{card.thumbnailUrl ? 'Replace thumbnail' : 'Set thumbnail'}</ListItemText>
            </MenuItem>
          )}
          {onDelete && (
            <MenuItem onClick={handleDeleteClick}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>

        <CardActionArea
          onClick={() => { if (card.url) window.open(card.url, '_blank', 'noopener'); }}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', cursor: card.url ? 'pointer' : 'default', p: 0 }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 10',
              overflow: 'hidden',
              bgcolor: '#F3F4F6',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {thumbUploading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.7)',
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            {showImage ? (
              <Box
                component="img"
                src={thumbSrc!}
                alt={`Preview of ${card.title}`}
                loading="lazy"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
                onError={() => setThumbFailed(true)}
              />
            ) : (
              // Branded placeholder -- never leave a blank grey box.
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  px: 2,
                  background: `linear-gradient(135deg, ${typeInfo.color}14, ${typeInfo.color}05)`,
                }}
              >
                <TypeIcon sx={{ fontSize: 36, color: typeInfo.color, opacity: 0.85 }} />
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textAlign: 'center',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {card.title}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <TypeIcon sx={{ fontSize: 16, color: typeInfo.color }} />
              {card.accessRestricted && (
                <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              )}
            </Box>

            <Typography
              sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}
            >
              {card.title}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                fontSize: '0.8125rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mb: 1.5,
                minHeight: '2.4em',
              }}
            >
              {card.summary}
            </Typography>

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.disabled" fontSize="0.75rem">
                {formatDate(card.lastModified)}
              </Typography>
              {card.url && <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
            </Box>
          </Box>
        </CardActionArea>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete this card?"
        message={`"${card.title}" will be permanently removed.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
